// src/pages/AdminDashboard.js
import React, { useEffect, useState, useRef } from 'react';
import { useAdmin } from '../contexts/AdminContext';
import { getFunctions, httpsCallable } from 'firebase/functions';  
import { auth } from '../firebase';

const AdminDashboard = () => {
  const { 
    isAdmin, 
    loading, 
    orders, 
    fetchOrders, 
    currentUser, 
    uploadSong,
    updateOrderStatus,
    fetchUserById,
    updateOrderLyrics,
    setMainSongVersion,
    deleteOrder,
    ordersReceived,
    revisionsRequested,
    completedOrders,
    searchOrders
  } = useAdmin();

  const lyricsTextareaRef = useRef(null);

  const getIdToken = async () => {
    if (currentUser) {
      try {
        const token = await auth.currentUser.getIdToken();
        console.log('Firebase ID Token:', token);
        alert('Token logged to console. Copy it from the Console tab.');
      } catch (error) {
        console.error('Error getting token:', error);
        alert('Failed to get token: ' + error.message);
      }
    } else {
      alert('No user logged in. Please log in as an admin.');
    }
  };

  const cleanupOrders = async () => {
    try {
      const functions = getFunctions();
      const cleanAllOrders = httpsCallable(functions, 'cleanAllOrders');
      const result = await cleanAllOrders({});
      console.log('Cleanup result:', result.data);
      alert(`Cleaned ${result.data.processedCount} orders`);
    } catch (error) {
      console.error('Cleanup error:', error);
      alert('Failed to clean orders');
    }
  };

  const [activeTab, setActiveTab] = useState('ordersReceived');
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [statusNote, setStatusNote] = useState('');
  const [userData, setUserData] = useState(null);
  const [savingLyrics, setSavingLyrics] = useState(false);
  const [songName, setSongName] = useState('');
  const [songDescription, setSongDescription] = useState('');
  const [revisionNote, setRevisionNote] = useState('');
  const [updatingRevision, setUpdatingRevision] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const loadOrders = async () => {
      if (isAdmin && !loading) {
        try {
          await fetchOrders();
        } catch (error) {
          console.error('Error loading orders:', error);
        }
      }
    };
    loadOrders();
  }, [isAdmin, loading, fetchOrders]);

  useEffect(() => {
    let isMounted = true;
    const fetchUserData = async () => {
      if (selectedOrderId) {
        const selectedOrder = orders.find(order => order.id === selectedOrderId);
        if (selectedOrder && selectedOrder.userId) {
          try {
            const user = await fetchUserById(selectedOrder.userId);
            if (isMounted) setUserData(user);
          } catch (error) {
            console.error('Error fetching user data:', error);
            if (isMounted) setUserData(null);
          }
        } else {
          if (isMounted) setUserData(null);
        }
      }
    };
    fetchUserData();
    return () => { isMounted = false; };
  }, [selectedOrderId, orders, fetchUserById]);

  const handleSearch = () => {
    if (searchTerm.trim()) {
      setIsSearching(true);
      const results = searchOrders(searchTerm);
      console.log(`Search for "${searchTerm}" found ${results.length} results`);
      setSearchResults(results);
    } else {
      setIsSearching(false);
      setSearchResults([]);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setIsSearching(false);
    setSearchResults([]);
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <div style={{ width: '40px', height: '40px', border: '4px solid #ccc', borderTopColor: '#F58800', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
  </div>;
  if (!isAdmin) return <div style={{ textAlign: 'center', padding: '2rem', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>Access Denied</div>;

  const selectedOrder = selectedOrderId ? orders.find(order => order.id === selectedOrderId) : null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    if (typeof dateString === 'object' && dateString !== null) {
      if (dateString.earliest && dateString.latest) {
        const earliestDate = new Date(dateString.earliest);
        const latestDate = new Date(dateString.latest);
        const earliestFormatted = earliestDate.toLocaleDateString();
        const latestFormatted = latestDate.toLocaleDateString();
        return earliestFormatted === latestFormatted ? earliestFormatted : `${earliestFormatted} - ${latestFormatted}`;
      }
      return 'Processing';
    }
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Order Received': return { bg: '#ffecb3', text: '#ff8f00' };
      case 'Writing': return { bg: '#bbdefb', text: '#1976d2' };
      case 'Recording': return { bg: '#d1c4e9', text: '#7b1fa2' };
      case 'Mixing': return { bg: '#b2ebf2', text: '#0097a7' };
      case 'Completed': return { bg: '#c8e6c9', text: '#2e7d32' };
      case 'Revision Requested': return { bg: '#fff9c4', text: '#f9a825' };
      default: return { bg: '#e0e0e0', text: '#757575' };
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedOrder) return;
    try {
      await updateOrderStatus(selectedOrder.id, newStatus, statusNote);
      setStatusNote('');
      await fetchOrders();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status: ' + error.message);
    }
  };

  const handleSaveLyrics = async () => {
    if (!selectedOrder) return;
    try {
      setSavingLyrics(true);
      const lyricsValue = lyricsTextareaRef.current.value;
      await updateOrderLyrics(selectedOrder.id, lyricsValue);
      alert('Lyrics saved successfully!');
    } catch (error) {
      console.error('Error saving lyrics:', error);
      alert('Failed to save lyrics: ' + error.message);
    } finally {
      setSavingLyrics(false);
    }
  };

  const handleSongUpload = async () => {
    try {
      if (!selectedFile) return;
      setUploading(true);
      const songMetadata = {
        name: songName || selectedFile.name,
        description: songDescription || '',
        uploadedAt: new Date().toISOString()
      };
      await uploadSong(selectedOrder.id, selectedFile, songMetadata);
      setSelectedFile(null);
      setSongName('');
      setSongDescription('');
      await fetchOrders();
      alert('Song uploaded successfully!');
    } catch (error) {
      console.error('Error uploading song:', error);
      alert('Failed to upload song: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleRevisionUpdate = async (newStatus) => {
    if (!selectedOrder || !revisionNote.trim()) {
      alert('Please provide revision notes');
      return;
    }
    try {
      setUpdatingRevision(true);
      const revisionCount = selectedOrder.revisions?.length || 0;
      await updateOrderStatus(selectedOrder.id, newStatus, revisionNote, {
        revisions: [
          ...(selectedOrder.revisions || []),
          {
            note: revisionNote,
            status: newStatus,
            timestamp: new Date().toISOString(),
            revisionNumber: revisionCount + 1
          }
        ],
        revisionsRemaining: Math.max(0, 3 - (revisionCount + 1))
      });
      setRevisionNote('');
      await fetchOrders();
      alert('Revision status updated successfully!');
    } catch (error) {
      console.error('Error updating revision status:', error);
      alert('Failed to update revision status: ' + error.message);
    } finally {
      setUpdatingRevision(false);
    }
  };

  const packageAllowsRevisions = (packageType) => {
    return ['Premium', 'Deluxe'].includes(packageType);
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order? This action cannot be undone.")) return;
    try {
      setIsDeleting(true);
      console.log(`Attempting to delete order: ${orderId}`);
      const result = await deleteOrder(orderId);
      console.log('Delete result:', result);
      if (selectedOrderId === orderId) {
        setSelectedOrderId(null);
        setUserData(null);
      }
      alert('Order deleted successfully');
    } catch (error) {
      console.error('Detailed delete error:', error);
      alert(`Failed to delete order: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div style={{
      width: '100vw',
      margin: 0,
      padding: '3rem 2rem',
      boxSizing: 'border-box',
      background: 'linear-gradient(135deg, #7C3AED 0%, #9D4EDD 100%)',
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
      // Before pseudo-element
      '::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(to bottom, rgba(255,255,255,0.1), rgba(255,255,255,0))',
        zIndex: 0
      },
      // After pseudo-element
      '::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'url("data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"50\" height=\"50\" viewBox=\"0 0 50 50\" opacity=\"0.05\"><text x=\"10\" y=\"40\" font-size=\"40\" fill=\"white\">♪</text></svg>") repeat',
        zIndex: 0
      }
    }}>
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '2rem', margin: 0, fontWeight: 700, color: '#ffffff', textShadow: '0 0 10px rgba(124,58,237,0.5)' }}>
            Admin Dashboard
          </h1>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={getIdToken}
              style={{
                backgroundColor: '#F58800',
                color: '#fff',
                border: 'none',
                padding: '0.75rem 1.25rem',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease',
                fontSize: '1rem',
                fontWeight: 500,
                boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#FFAA33'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#F58800'}
            >
              Get Firebase ID Token
            </button>
          </div>
        </div>
        <p style={{ color: '#ffffff' }}>Welcome to the MakeMeSongs admin dashboard, {currentUser?.email}!</p>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
          {['ordersReceived', 'revisionsRequested', 'completedOrders', 'analytics', 'settings'].map(tab => {
            let displayName;
            switch(tab) {
              case 'ordersReceived': displayName = `Orders Received ${isSearching ? '(Searching)' : `(${ordersReceived.length})`}`; break;
              case 'revisionsRequested': displayName = `Revisions Requested ${isSearching ? '(Searching)' : `(${revisionsRequested.length})`}`; break;
              case 'completedOrders': displayName = `Completed Orders ${isSearching ? '(Searching)' : `(${completedOrders.length})`}`; break;
              default: displayName = tab.charAt(0).toUpperCase() + tab.slice(1);
            }
            return (
              <button 
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  clearSearch();
                  setSelectedOrderId(null);
                }}
                style={{
                  backgroundColor: activeTab === tab ? '#7C3AED' : '#F58800',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: '#ffffff',
                  transition: 'all 0.3s',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  borderBottom: activeTab === tab ? '2px solid #ffffff' : 'none'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = activeTab === tab ? '#6D28D9' : '#FFAA33'}
                onMouseOut={(e) => e.target.style.backgroundColor = activeTab === tab ? '#7C3AED' : '#F58800'}
              >
                {displayName}
              </button>
            );
          })}
        </div>

        {/* Search Bar */}
        {['ordersReceived', 'revisionsRequested', 'completedOrders'].includes(activeTab) && !selectedOrderId && (
          <div style={{ 
            display: 'flex', 
            marginBottom: '20px', 
            alignItems: 'center',
            backgroundColor: 'white',
            padding: '10px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <input
              type="text"
              placeholder="Search orders by recipient, package, or order number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              style={{
                flex: 1,
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                marginRight: '10px',
                fontSize: '1rem'
              }}
            />
            <button
              onClick={handleSearch}
              style={{
                padding: '8px 16px',
                backgroundColor: '#F58800',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#FFAA33'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#F58800'}
            >
              Search
            </button>
            {isSearching && (
              <button
                onClick={clearSearch}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ffffff',
                  color: '#333',
                  border: '1px solid #7C3AED',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginLeft: '10px',
                  transition: 'background-color 0.3s, border-color 0.3s',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#F5F3FF';
                  e.target.style.borderColor = '#6D28D9';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#ffffff';
                  e.target.style.borderColor = '#7C3AED';
                }}
              >
                Clear
              </button>
            )}
          </div>
        )}

        {/* Search Status Indicators */}
        {isSearching && searchResults.length > 0 && (
          <div style={{
            padding: '10px 15px',
            backgroundColor: '#e8f5e9',
            borderRadius: '4px',
            marginBottom: '15px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ color: '#388e3c' }}>
              Found <strong>{searchResults.length}</strong> results for "<em>{searchTerm}</em>"
            </span>
            <button
              onClick={clearSearch}
              style={{
                padding: '6px 12px',
                backgroundColor: 'transparent',
                color: '#388e3c',
                border: '1px solid #388e3c',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'background-color 0.3s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#e8f5e9'}
              onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              Clear Search
            </button>
          </div>
        )}

        {isSearching && searchResults.length === 0 && (
          <div style={{
            padding: '20px 15px',
            backgroundColor: '#fff3e0',
            borderRadius: '4px',
            marginBottom: '15px',
            textAlign: 'center'
          }}>
            <p style={{ margin: '0', fontWeight: 'bold', color: '#f57c00' }}>
              No results found for "{searchTerm}"
            </p>
            <p style={{ margin: '10px 0 0 0', fontSize: '14px' }}>
              Try a different search term or{' '}
              <button 
                onClick={clearSearch}
                style={{
                  padding: '0',
                  backgroundColor: 'transparent',
                  color: '#f57c00',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '14px'
                }}
              >
                clear your search
              </button>
            </p>
          </div>
        )}

        {/* Orders Received Tab */}
        {activeTab === 'ordersReceived' && !selectedOrderId && (
          <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', animation: 'fadeIn 0.5s ease-in' }}>
            <h2 style={{ color: '#2D3748', fontSize: '1.5rem' }}>New Orders Management</h2>
            <p style={{ color: '#666' }}>Orders with status "Order Received": {ordersReceived.length}</p>
            <button 
              onClick={getIdToken}
              style={{
                padding: '8px 16px',
                backgroundColor: '#F58800',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginBottom: '20px',
                transition: 'background-color 0.3s ease',
                fontWeight: 500,
                boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#FFAA33'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#F58800'}
            >
              Get Firebase ID Token
            </button>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F5F3FF' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Order #</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Customer</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Recipient</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Package</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Date</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(isSearching ? searchResults : ordersReceived).map(order => (
                    <tr key={order.id} style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '12px', color: '#4A5568' }}>{order.orderNumber || 'N/A'}</td>
                      <td style={{ padding: '12px', color: '#4A5568' }}>{order.userData?.name || 'Unknown'}</td>
                      <td style={{ padding: '12px', color: '#4A5568' }}>{order.recipientName || 'N/A'}</td>
                      <td style={{ padding: '12px', color: '#4A5568' }}>{order.package || 'N/A'}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ 
                          padding: '5px 10px',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: 500,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          backgroundColor: getStatusColor(order.status).bg,
                          color: getStatusColor(order.status).text
                        }}>
                          {order.status || 'Unknown'}
                        </span>
                      </td>
                      <td style={{ padding: '12px', color: '#4A5568' }}>{formatDate(order.createdAt)}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <button 
                            onClick={() => setSelectedOrderId(order.id)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#ffffff',
                              color: '#333',
                              border: '2px solid #7C3AED',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: 500,
                              transition: 'all 0.3s ease',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                            onMouseOver={(e) => {
                              e.target.style.backgroundColor = '#F5F3FF';
                              e.target.style.borderColor = '#6D28D9';
                              e.target.style.transform = 'translateY(-2px)';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.backgroundColor = '#ffffff';
                              e.target.style.borderColor = '#7C3AED';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            View Details
                          </button>
                          <button
                            onClick={() => handleDeleteOrder(order.id)}
                            disabled={isDeleting}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#d32f2f',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: isDeleting ? 'not-allowed' : 'pointer',
                              opacity: isDeleting ? 0.7 : 1,
                              transition: 'background-color 0.3s',
                              fontWeight: 500,
                              boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
                            }}
                            onMouseOver={(e) => !isDeleting && (e.target.style.backgroundColor = '#b71c1c')}
                            onMouseOut={(e) => !isDeleting && (e.target.style.backgroundColor = '#d32f2f')}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(isSearching ? searchResults : ordersReceived).length === 0 && (
                    <tr>
                      <td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        {isSearching ? 'No search results found.' : 'No new orders at this time.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Revisions Requested Tab */}
        {activeTab === 'revisionsRequested' && !selectedOrderId && (
          <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', animation: 'fadeIn 0.5s ease-in' }}>
            <h2 style={{ color: '#2D3748', fontSize: '1.5rem' }}>Revision Requests Management</h2>
            <p style={{ color: '#666' }}>Orders with status "Revision Requested": {revisionsRequested.length}</p>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F5F3FF' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Order #</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Customer</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Recipient</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Package</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Revisions</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Last Update</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(isSearching ? searchResults : revisionsRequested).map(order => (
                    <tr key={order.id} style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '12px', color: '#4A5568' }}>{order.orderNumber || 'N/A'}</td>
                      <td style={{ padding: '12px', color: '#4A5568' }}>{order.userData?.name || 'Unknown'}</td>
                      <td style={{ padding: '12px', color: '#4A5568' }}>{order.recipientName || 'N/A'}</td>
                      <td style={{ padding: '12px', color: '#4A5568' }}>{order.package || 'N/A'}</td>
                      <td style={{ padding: '12px', color: '#4A5568' }}>
                        <span style={{ fontWeight: 'bold' }}>{order.revisions?.length || 0}/3</span>
                      </td>
                      <td style={{ padding: '12px', color: '#4A5568' }}>{formatDate(order.statusUpdatedAt)}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <button 
                            onClick={() => setSelectedOrderId(order.id)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#ffffff',
                              color: '#333',
                              border: '2px solid #7C3AED',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: 500,
                              transition: 'all 0.3s ease',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                            onMouseOver={(e) => {
                              e.target.style.backgroundColor = '#F5F3FF';
                              e.target.style.borderColor = '#6D28D9';
                              e.target.style.transform = 'translateY(-2px)';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.backgroundColor = '#ffffff';
                              e.target.style.borderColor = '#7C3AED';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            View Details
                          </button>
                          <button
                            onClick={() => handleDeleteOrder(order.id)}
                            disabled={isDeleting}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#d32f2f',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: isDeleting ? 'not-allowed' : 'pointer',
                              opacity: isDeleting ? 0.7 : 1,
                              transition: 'background-color 0.3s',
                              fontWeight: 500,
                              boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
                            }}
                            onMouseOver={(e) => !isDeleting && (e.target.style.backgroundColor = '#b71c1c')}
                            onMouseOut={(e) => !isDeleting && (e.target.style.backgroundColor = '#d32f2f')}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(isSearching ? searchResults : revisionsRequested).length === 0 && (
                    <tr>
                      <td colSpan="6" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        {isSearching ? 'No search results found.' : 'No revision requests at this time.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Completed Orders Tab */}
        {activeTab === 'completedOrders' && !selectedOrderId && (
          <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', animation: 'fadeIn 0.5s ease-in' }}>
            <h2 style={{ color: '#2D3748', fontSize: '1.5rem' }}>Completed Orders Management</h2>
            <p style={{ color: '#666' }}>Orders with status "Completed": {completedOrders.length}</p>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F5F3FF' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Order #</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Customer</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Recipient</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Package</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Delivery Date</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Revisions</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd', color: '#2D3748' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(isSearching ? searchResults : completedOrders).map(order => (
                    <tr key={order.id} style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '12px', color: '#4A5568' }}>{order.orderNumber || 'N/A'}</td>
                      <td style={{ padding: '12px', color: '#4A5568' }}>{order.userData?.name || 'Unknown'}</td>
                      <td style={{ padding: '12px', color: '#4A5568' }}>{order.recipientName || 'N/A'}</td>
                      <td style={{ padding: '12px', color: '#4A5568' }}>{order.package || 'N/A'}</td>
                      <td style={{ padding: '12px', color: '#4A5568' }}>{formatDate(order.completedAt || order.songUploadedAt || order.estimatedDelivery)}</td>
                      <td style={{ padding: '12px', color: '#4A5568' }}>{order.revisions?.length ? `${order.revisions.length}/3` : '0/3'}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <button 
                            onClick={() => setSelectedOrderId(order.id)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#ffffff',
                              color: '#333',
                              border: '2px solid #7C3AED',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: 500,
                              transition: 'all 0.3s ease',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                            onMouseOver={(e) => {
                              e.target.style.backgroundColor = '#F5F3FF';
                              e.target.style.borderColor = '#6D28D9';
                              e.target.style.transform = 'translateY(-2px)';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.backgroundColor = '#ffffff';
                              e.target.style.borderColor = '#7C3AED';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            View Details
                          </button>
                          <button
                            onClick={() => handleDeleteOrder(order.id)}
                            disabled={isDeleting}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#d32f2f',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: isDeleting ? 'not-allowed' : 'pointer',
                              opacity: isDeleting ? 0.7 : 1,
                              transition: 'background-color 0.3s',
                              fontWeight: 500,
                              boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
                            }}
                            onMouseOver={(e) => !isDeleting && (e.target.style.backgroundColor = '#b71c1c')}
                            onMouseOut={(e) => !isDeleting && (e.target.style.backgroundColor = '#d32f2f')}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(isSearching ? searchResults : completedOrders).length === 0 && (
                    <tr>
                      <td colSpan="6" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        {isSearching ? 'No search results found.' : 'No completed orders at this time.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Order Detail View */}
        {selectedOrder && (
          <div style={{ animation: 'fadeIn 0.5s ease-in' }}>
            <button 
              onClick={() => {
                setSelectedOrderId(null);
                setUserData(null);
                setRevisionNote('');
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#ffffff',
                color: '#333',
                border: '1px solid #7C3AED',
                borderRadius: '4px',
                cursor: 'pointer',
                marginBottom: '20px',
                transition: 'background-color 0.3s, border-color 0.3s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#F5F3FF';
                e.target.style.borderColor = '#6D28D9';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#ffffff';
                e.target.style.borderColor = '#7C3AED';
              }}
            >
              ← Back to {activeTab === 'ordersReceived' ? 'new orders' : 
                         activeTab === 'revisionsRequested' ? 'revision requests' : 
                         activeTab === 'completedOrders' ? 'completed orders' : 'orders'}
            </button>

            <div style={{ 
              backgroundColor: 'white', 
              padding: '20px', 
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', backgroundColor: '#F5F3FF', padding: '0.75rem 1rem', borderRadius: '6px' }}>
                <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#2D3748' }}>Order Details: {selectedOrder.orderNumber}</h3>
                <button
                  onClick={() => handleDeleteOrder(selectedOrder.id)}
                  disabled={isDeleting}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#d32f2f',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                    opacity: isDeleting ? 0.7 : 1,
                    transition: 'background-color 0.3s',
                    fontWeight: 500,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
                  }}
                  onMouseOver={(e) => !isDeleting && (e.target.style.backgroundColor = '#b71c1c')}
                  onMouseOut={(e) => !isDeleting && (e.target.style.backgroundColor = '#d32f2f')}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Order'}
                </button>
              </div>

              <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
                {/* Customer Information */}
                <div style={{ flex: '1', minWidth: '300px' }}>
                  <h4 style={{ borderBottom: '1px solid #eee', paddingBottom: '8px', color: '#2D3748' }}>Customer Information</h4>
                  {userData && (
                    <>
                      <p style={{ fontSize: '1rem', color: '#4A5568' }}><strong style={{ color: '#2D3748' }}>Customer:</strong> {userData.name || 'Unknown User'}</p>
                      <p style={{ fontSize: '1rem', color: '#4A5568' }}><strong style={{ color: '#2D3748' }}>Email:</strong> {userData.email || 'No email available'}</p>
                    </>
                  )}
                  <p style={{ fontSize: '1rem', color: '#4A5568' }}><strong style={{ color: '#2D3748' }}>Order ID:</strong> {selectedOrder.id}</p>
                  <p style={{ fontSize: '1rem', color: '#4A5568' }}><strong style={{ color: '#2D3748' }}>Recipient:</strong> {selectedOrder.recipientName || 'N/A'}</p>
                  <p style={{ fontSize: '1rem', color: '#4A5568' }}><strong style={{ color: '#2D3748' }}>User ID:</strong> {selectedOrder.userId || 'N/A'}</p>
                  <p style={{ fontSize: '1rem', color: '#4A5568' }}><strong style={{ color: '#2D3748' }}>Order Date:</strong> {formatDate(selectedOrder.createdAt)}</p>
                  <p style={{ fontSize: '1rem', color: '#4A5568' }}><strong style={{ color: '#2D3748' }}>Est. Delivery:</strong> {formatDate(selectedOrder.estimatedDelivery)}</p>

                  <h4 style={{ borderBottom: '1px solid #eee', paddingBottom: '8px', marginTop: '20px', color: '#2D3748' }}>Status</h4>
                  <p style={{ fontSize: '1rem', color: '#4A5568' }}>
                    <strong style={{ color: '#2D3748' }}>Current Status:</strong> 
                    <span style={{ 
                      display: 'inline-block',
                      padding: '5px 10px',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      backgroundColor: getStatusColor(selectedOrder.status).bg,
                      color: getStatusColor(selectedOrder.status).text,
                      marginLeft: '10px'
                    }}>
                      {selectedOrder.status || 'Unknown'}
                    </span>
                  </p>
                  {selectedOrder.statusUpdatedAt && (
                    <p style={{ fontSize: '1rem', color: '#4A5568' }}><strong style={{ color: '#2D3748' }}>Last Updated:</strong> {formatDate(selectedOrder.statusUpdatedAt)}</p>
                  )}
                  {selectedOrder.statusNote && (
                    <p style={{ fontSize: '1rem', color: '#4A5568' }}><strong style={{ color: '#2D3748' }}>Status Note:</strong> {selectedOrder.statusNote}</p>
                  )}

                  <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#F5F3FF', borderRadius: '8px' }}>
                    <h4 style={{ marginTop: 0, color: '#2D3748' }}>Update Status</h4>
                    <textarea
                      placeholder="Add status note (optional)"
                      value={statusNote}
                      onChange={(e) => setStatusNote(e.target.value)}
                      style={{ 
                        width: '100%', 
                        padding: '8px', 
                        borderRadius: '4px', 
                        border: '1px solid #ddd',
                        minHeight: '80px',
                        fontSize: '1rem'
                      }}
                    />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                      {['Order Received', 'Writing', 'Recording', 'Mixing', 'Completed'].map(status => (
                        <button
                          key={status}
                          onClick={() => handleStatusChange(status)}
                          disabled={selectedOrder.status === status}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: selectedOrder.status === status ? '#e0e0e0' : '#F58800',
                            color: selectedOrder.status === status ? '#666' : 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: selectedOrder.status === status ? 'default' : 'pointer',
                            opacity: selectedOrder.status === status ? 0.7 : 1,
                            transition: 'background-color 0.3s',
                            fontWeight: 500,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}
                          onMouseOver={(e) => selectedOrder.status !== status && (e.target.style.backgroundColor = '#FFAA33')}
                          onMouseOut={(e) => selectedOrder.status !== status && (e.target.style.backgroundColor = '#F58800')}
                        >
                          {status}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Revision Management Section */}
                  {selectedOrder.songs && selectedOrder.songs.length > 0 && packageAllowsRevisions(selectedOrder.package) && (
                    <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#F5F3FF', borderRadius: '8px' }}>
                      <h4 style={{ marginTop: 0, color: '#2D3748' }}>Revision Management</h4>
                      <p style={{ fontSize: '1rem', color: '#4A5568' }}><strong style={{ color: '#2D3748' }}>Revisions Used:</strong> {selectedOrder.revisions?.length || 0} / 3</p>
                      <p style={{ fontSize: '1rem', color: '#4A5568' }}><strong style={{ color: '#2D3748' }}>Revisions Remaining:</strong> {selectedOrder.revisionsRemaining ?? 3}</p>

                      {selectedOrder.revisions && selectedOrder.revisions.length > 0 && (
                        <div style={{ marginBottom: '15px' }}>
                          <h5 style={{ margin: '10px 0', color: '#2D3748' }}>Revision History</h5>
                          {selectedOrder.revisions.map((revision, index) => (
                            <div key={index} style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                              <p style={{ fontSize: '1rem', color: '#4A5568' }}><strong style={{ color: '#2D3748' }}>Revision #{revision.revisionNumber}</strong> - {formatDate(revision.timestamp)}</p>
                              <p style={{ fontSize: '1rem', color: '#4A5568' }}><strong style={{ color: '#2D3748' }}>Status:</strong> {revision.status}</p>
                              <p style={{ whiteSpace: 'pre-wrap', fontSize: '1rem', color: '#4A5568' }}>{revision.note}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {(selectedOrder.revisions?.length || 0) < 3 && (
                        <>
                          <textarea
                            placeholder="Enter revision notes (required for revision status)"
                            value={revisionNote}
                            onChange={(e) => setRevisionNote(e.target.value)}
                            style={{ 
                              width: '100%', 
                              padding: '8px', 
                              borderRadius: '4px', 
                              border: '1px solid #ddd', 
                              minHeight: '80px', 
                              marginBottom: '10px',
                              fontSize: '1rem'
                            }}
                          />
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => handleRevisionUpdate('Revision Requested')}
                              disabled={updatingRevision || selectedOrder.status === 'Revision Requested'}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: selectedOrder.status === 'Revision Requested' ? '#e0e0e0' : '#F58800',
                                color: selectedOrder.status === 'Revision Requested' ? '#666' : 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: selectedOrder.status === 'Revision Requested' ? 'default' : 'pointer',
                                opacity: updatingRevision || selectedOrder.status === 'Revision Requested' ? 0.7 : 1,
                                transition: 'background-color 0.3s',
                                fontWeight: 500,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                              }}
                              onMouseOver={(e) => !updatingRevision && selectedOrder.status !== 'Revision Requested' && (e.target.style.backgroundColor = '#FFAA33')}
                              onMouseOut={(e) => !updatingRevision && selectedOrder.status !== 'Revision Requested' && (e.target.style.backgroundColor = '#F58800')}
                            >
                              Mark as Revision Requested
                            </button>
                            <button
                              onClick={() => handleRevisionUpdate('Completed')}
                              disabled={updatingRevision}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#F58800',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: updatingRevision ? 'default' : 'pointer',
                                opacity: updatingRevision ? 0.7 : 1,
                                transition: 'background-color 0.3s',
                                fontWeight: 500,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                              }}
                              onMouseOver={(e) => !updatingRevision && (e.target.style.backgroundColor = '#FFAA33')}
                              onMouseOut={(e) => !updatingRevision && (e.target.style.backgroundColor = '#F58800')}
                            >
                              Complete Revision
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Song Details */}
                <div style={{ flex: '1', minWidth: '300px' }}>
                  <h4 style={{ borderBottom: '1px solid #eee', paddingBottom: '8px', color: '#2D3748' }}>Song Details</h4>
                  <p style={{ fontSize: '1rem', color: '#4A5568' }}><strong style={{ color: '#2D3748' }}>Package:</strong> {selectedOrder.package || 'N/A'}</p>
                  <p style={{ fontSize: '1rem', color: '#4A5568' }}>
                    <strong style={{ color: '#2D3748' }}>Genres:</strong> 
                    {selectedOrder.genres ? selectedOrder.genres.map(genre => (
                      <span key={genre} style={{ display: 'inline-block', backgroundColor: '#F5F3FF', color: '#7C3AED', padding: '0.25rem 0.5rem', borderRadius: '4px', marginRight: '0.25rem', fontSize: '0.85rem' }}>
                        {genre}
                      </span>
                    )) : 'N/A'}
                  </p>
                  <p style={{ fontSize: '1rem', color: '#4A5568' }}><strong style={{ color: '#2D3748' }}>Occasion:</strong> {selectedOrder.occasion || 'N/A'}</p>
                  <p style={{ fontSize: '1rem', color: '#4A5568' }}><strong style={{ color: '#2D3748' }}>Rush Delivery:</strong> <span style={{ color: selectedOrder.rushDelivery ? '#f06292' : '#4A5568', fontWeight: 500 }}>{selectedOrder.rushDelivery ? 'Yes' : 'No'}</span></p>

                  {selectedOrder.songStory && (
                    <div style={{ marginTop: '20px', margin: '15px 0', borderRadius: '8px', overflow: 'hidden' }}>
                      <h4 style={{ color: '#6a3de8', fontSize: '1.3rem', marginTop: '25px', fontWeight: 600, borderBottom: '2px solid #6a3de8', paddingBottom: '8px' }}>Song Story</h4>
                      <div style={{ backgroundColor: '#f0f7ff', borderLeft: '4px solid #6a3de8', padding: '15px', borderRadius: '4px' }}>
                        <p style={{ whiteSpace: 'pre-line', lineHeight: '1.6', margin: 0, color: '#4A5568' }}>{selectedOrder.songStory}</p>
                      </div>
                    </div>
                  )}

                  {selectedOrder.keyPoints && (
                    <div style={{ marginTop: '20px', margin: '15px 0', borderRadius: '8px', overflow: 'hidden' }}>
                      <h4 style={{ color: '#6a3de8', fontSize: '1.3rem', marginTop: '25px', fontWeight: 600, borderBottom: '2px solid #6a3de8', paddingBottom: '8px' }}>Key Points</h4>
                      <div style={{ backgroundColor: '#fff9f0', borderLeft: '3px solid #f5a623', padding: '12px', borderRadius: '4px', marginTop: '8px' }}>
                        <p style={{ whiteSpace: 'pre-line', lineHeight: '1.6', margin: 0, color: '#4A5568' }}>{selectedOrder.keyPoints}</p>
                      </div>
                    </div>
                  )}

                  {/* Lyrics Section */}
                  <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #eee' }}>
                    <h4 style={{ marginTop: 0, color: '#2D3748' }}>Song Lyrics</h4>
                    <textarea
                      placeholder="Enter the finished lyrics here..."
                      defaultValue={selectedOrder.lyrics || ''}
                      ref={lyricsTextareaRef}
                      style={{ 
                        width: '100%', 
                        padding: '8px', 
                        borderRadius: '4px', 
                        border: '1px solid #ddd',
                        minHeight: '150px',
                        fontFamily: 'inherit',
                        fontSize: '16px',
                        color: '#333',
                        lineHeight: '1.6'
                      }}
                    />
                    <button
                      onClick={handleSaveLyrics}
                      disabled={savingLyrics}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#F58800',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: savingLyrics ? 'not-allowed' : 'pointer',
                        marginTop: '10px',
                        opacity: savingLyrics ? 0.7 : 1,
                        transition: 'background-color 0.3s',
                        fontWeight: 500,
                        boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
                      }}
                      onMouseOver={(e) => !savingLyrics && (e.target.style.backgroundColor = '#FFAA33')}
                      onMouseOut={(e) => !savingLyrics && (e.target.style.backgroundColor = '#F58800')}
                    >
                      {savingLyrics ? 'Saving...' : 'Save Lyrics'}
                    </button>
                    {selectedOrder.lyricsUpdatedAt && (
                      <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                        Last updated: {formatDate(selectedOrder.lyricsUpdatedAt)}
                      </p>
                    )}
                  </div>

                  {/* Songs Section */}
                  <div style={{ marginTop: '20px' }}>
                    <h4 style={{ borderBottom: '1px solid #eee', paddingBottom: '8px', color: '#2D3748' }}>Songs</h4>
                    {selectedOrder.songs && selectedOrder.songs.length > 0 ? (
                      <div style={{ marginBottom: '20px', padding: '1.5rem', backgroundColor: '#F5F3FF', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        {selectedOrder.songs.map((song, index) => (
                          <div 
                            key={index}
                            style={{ 
                              marginBottom: index === selectedOrder.songs.length - 1 ? '0' : '15px',
                              padding: '15px',
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E2E8F0',
                              borderLeft: '4px solid #7C3AED',
                              borderRadius: '8px',
                              position: 'relative',
                              transition: 'box-shadow 0.3s ease, transform 0.2s ease',
                              minHeight: '180px'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.boxShadow = 'none';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingLeft: '2.5rem', paddingTop: '0.5rem', marginBottom: '0.9rem' }}>
                              <h5 style={{ margin: '0', fontSize: '1.1rem', fontWeight: 600, color: '#2D3748', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.5' }}>
                                {song.name || `Song ${index + 1}`}
                                {song.isMainVersion && (
                                  <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '8px', fontStyle: 'italic' }}>Main Version</span>
                                )}
                              </h5>
                              <span style={{ fontSize: '0.9rem', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.5', paddingLeft: '2.5rem', paddingTop: '0.5rem', marginBottom: '0.9rem' }}>
                                Uploaded: {formatDate(song.uploadedAt)}
                              </span>
                            </div>
                            {song.description && (
                              <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.5', paddingLeft: '2.5rem', paddingTop: '0.5rem', marginBottom: '0.9rem' }}>
                                {song.description}
                              </p>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem', marginBottom: '1.35rem', paddingBottom: '0.9rem', paddingLeft: '2.5rem' }}>
                              <audio controls style={{ width: '100%', marginBottom: '10px' }}>
                                <source src={song.url} type="audio/mpeg" />
                                Your browser does not support the audio element.
                              </audio>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', paddingLeft: '2.5rem', paddingTop: '0.5rem', marginBottom: '0.9rem' }}>
                              <a 
                                href={song.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                  padding: '8px 12px',
                                  backgroundColor: '#ffffff',
                                  color: '#333',
                                  border: '2px solid #7C3AED',
                                  borderRadius: '8px',
                                  fontWeight: 600,
                                  transition: 'all 0.2s ease',
                                  textDecoration: 'none',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  position: 'relative',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                                onMouseOver={(e) => {
                                  e.target.style.backgroundColor = '#F5F3FF';
                                  e.target.style.transform = 'translateY(-2px)';
                                  e.target.style.boxShadow = '0 4px 8px rgba(124,58,237,0.2)';
                                }}
                                onMouseOut={(e) => {
                                  e.target.style.backgroundColor = '#ffffff';
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                }}
                              >
                                <span style={{ fontSize: '20px', marginBottom: '4px', color: '#7C3AED' }}>⬇</span>
                                <span style={{ fontSize: '14px', color: '#333' }}>Download</span>
                              </a>
                              {song.isMainVersion ? (
                                <span style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#e0e0e0',
                                  color: '#666',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  Main Version
                                </span>
                              ) : (
                                <button 
                                  onClick={async () => {
                                    try {
                                      await setMainSongVersion(selectedOrder.id, index);
                                      await fetchOrders();
                                    } catch (error) {
                                      alert('Failed to set main version: ' + error.message);
                                    }
                                  }}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#ffffff',
                                    color: '#7C3AED',
                                    border: '1px solid #7C3AED',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }}
                                  onMouseOver={(e) => {
                                    e.target.style.backgroundColor = '#F5F3FF';
                                    e.target.style.borderColor = '#6D28D9';
                                  }}
                                  onMouseOut={(e) => {
                                    e.target.style.backgroundColor = '#ffffff';
                                    e.target.style.borderColor = '#7C3AED';
                                  }}
                                >
                                  Set as Main Version
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: '#666', fontStyle: 'italic' }}>No songs uploaded yet</p>
                    )}

                    {/* Song Upload */}
                    <div style={{ padding: '15px', backgroundColor: '#F5F3FF', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                      <h4 style={{ marginTop: 0, color: '#2D3748' }}>Upload New Song</h4>
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#2D3748' }}>
                          Song Name (optional):
                        </label>
                        <input
                          type="text"
                          placeholder="E.g., Final Version, Acoustic Version, etc."
                          value={songName}
                          onChange={(e) => setSongName(e.target.value)}
                          style={{ 
                            width: '100%', 
                            padding: '10px', 
                            borderRadius: '4px', 
                            border: '1px solid #ddd',
                            marginBottom: '10px',
                            fontSize: '16px'
                          }}
                        />
                      </div>
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#2D3748' }}>
                          Description (optional):
                        </label>
                        <textarea
                          placeholder="Brief description of this version"
                          value={songDescription}
                          onChange={(e) => setSongDescription(e.target.value)}
                          style={{ 
                            width: '100%', 
                            padding: '8px', 
                            borderRadius: '4px', 
                            border: '1px solid #ddd',
                            minHeight: '60px',
                            fontSize: '1rem'
                          }}
                        />
                      </div>
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#2D3748' }}>
                          Song File:
                        </label>
                        <input
                          type="file"
                          accept="audio/mp3,audio/mpeg"
                          onChange={(e) => e.target.files && setSelectedFile(e.target.files[0])}
                          style={{ display: 'block', marginBottom: '10px' }}
                        />
                      </div>
                      {selectedFile && (
                        <div style={{ marginTop: '10px' }}>
                          <p style={{ color: '#4A5568' }}>Selected file: {selectedFile.name}</p>
                          <button
                            onClick={handleSongUpload}
                            disabled={uploading}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#F58800',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: uploading ? 'not-allowed' : 'pointer',
                              marginTop: '10px',
                              opacity: uploading ? 0.7 : 1,
                              transition: 'background-color 0.3s',
                              fontWeight: 500,
                              boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
                            }}
                            onMouseOver={(e) => !uploading && (e.target.style.backgroundColor = '#FFAA33')}
                            onMouseOut={(e) => !uploading && (e.target.style.backgroundColor = '#F58800')}
                          >
                            {uploading ? 'Uploading...' : 'Upload Song'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Default View */}
        {!selectedOrderId && !['ordersReceived', 'revisionsRequested', 'completedOrders', 'analytics', 'settings'].includes(activeTab) && (
          <div style={{ 
            padding: '30px', 
            textAlign: 'center', 
            backgroundColor: '#fff',
            borderRadius: '8px',
            marginTop: '20px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            animation: 'fadeIn 0.5s ease-in'
          }}>
            <h3 style={{ color: '#2D3748' }}>Please select an order category from the tabs above</h3>
            <p style={{ color: '#666' }}>We've organized your orders into three main categories:</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '20px', marginTop: '20px' }}>
              <div style={{ 
                flex: '1',
                minWidth: '250px',
                maxWidth: '300px',
                padding: '20px',
                backgroundColor: getStatusColor('Order Received').bg,
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <h4 style={{ color: getStatusColor('Order Received').text, marginTop: 0 }}>Orders Received</h4>
                <p style={{ color: '#4A5568' }}>New orders that need to be processed</p>
                <button
                  onClick={() => setActiveTab('ordersReceived')}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: getStatusColor('Order Received').text,
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#FFAA33'}
                  onMouseOut={(e) => e.target.style.backgroundColor = getStatusColor('Order Received').text}
                >
                  View New Orders ({ordersReceived.length})
                </button>
              </div>
              <div style={{ 
                flex: '1',
                minWidth: '250px',
                maxWidth: '300px',
                padding: '20px',
                backgroundColor: getStatusColor('Revision Requested').bg,
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <h4 style={{ color: getStatusColor('Revision Requested').text, marginTop: 0 }}>Revision Requested</h4>
                <p style={{ color: '#4A5568' }}>Orders where customers have requested changes</p>
                <button
                  onClick={() => setActiveTab('revisionsRequested')}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: getStatusColor('Revision Requested').text,
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#FFAA33'}
                  onMouseOut={(e) => e.target.style.backgroundColor = getStatusColor('Revision Requested').text}
                >
                  View Revisions ({revisionsRequested.length})
                </button>
              </div>
              <div style={{ 
                flex: '1',
                minWidth: '250px',
                maxWidth: '300px',
                padding: '20px',
                backgroundColor: getStatusColor('Completed').bg,
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <h4 style={{ color: getStatusColor('Completed').text, marginTop: 0 }}>Completed Orders</h4>
                <p style={{ color: '#4A5568' }}>Orders that have been delivered to customers</p>
                <button
                  onClick={() => setActiveTab('completedOrders')}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: getStatusColor('Completed').text,
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#48BB78'}
                  onMouseOut={(e) => e.target.style.backgroundColor = getStatusColor('Completed').text}
                >
                  View Completed ({completedOrders.length})
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', animation: 'fadeIn 0.5s ease-in' }}>
                        <h2 style={{ color: '#2D3748', fontSize: '1.5rem', marginBottom: '1rem' }}>Analytics</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#F5F3FF', 
                borderRadius: '8px', 
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)' 
              }}>
                <h3 style={{ color: '#7C3AED', margin: '0 0 10px 0' }}>Total Orders</h3>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2D3748', margin: 0 }}>{orders.length}</p>
              </div>
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#FFECB3', 
                borderRadius: '8px', 
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)' 
              }}>
                <h3 style={{ color: '#F58800', margin: '0 0 10px 0' }}>Orders Received</h3>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2D3748', margin: 0 }}>{ordersReceived.length}</p>
              </div>
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#FFF9C4', 
                borderRadius: '8px', 
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)' 
              }}>
                <h3 style={{ color: '#F9A825', margin: '0 0 10px 0' }}>Revisions Requested</h3>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2D3748', margin: 0 }}>{revisionsRequested.length}</p>
              </div>
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#C8E6C9', 
                borderRadius: '8px', 
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)' 
              }}>
                <h3 style={{ color: '#2E7D32', margin: '0 0 10px 0' }}>Completed Orders</h3>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2D3748', margin: 0 }}>{completedOrders.length}</p>
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div style={{ 
            backgroundColor: '#fff', 
            borderRadius: '8px', 
            padding: '1.5rem', 
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
            animation: 'fadeIn 0.5s ease-in' 
          }}>
            <h2 style={{ color: '#2D3748', fontSize: '1.5rem', marginBottom: '1rem' }}>Settings</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ 
                padding: '15px', 
                backgroundColor: '#F5F3FF', 
                borderRadius: '8px', 
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)' 
              }}>
                <h3 style={{ color: '#7C3AED', margin: '0 0 10px 0' }}>Admin Tools</h3>
                <button
                  onClick={cleanupOrders}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#F58800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease',
                    fontWeight: 500,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#FFAA33'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#F58800'}
                >
                  Clean Up Orders
                </button>
                <p style={{ color: '#666', margin: '10px 0 0 0' }}>
                  Removes stale or invalid order data (use with caution)
                </p>
              </div>
              <div style={{ 
                padding: '15px', 
                backgroundColor: '#F5F3FF', 
                borderRadius: '8px', 
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)' 
              }}>
                <h3 style={{ color: '#7C3AED', margin: '0 0 10px 0' }}>Account</h3>
                <p style={{ color: '#4A5568' }}>
                  Logged in as: <strong>{currentUser?.email}</strong>
                </p>
                <button
                  onClick={() => auth.signOut()}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#d32f2f',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease',
                    fontWeight: 500,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#b71c1c'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#d32f2f'}
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;